import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Smile, Image, FileText, Mic, X } from 'lucide-react';
import { InlineSkeleton } from '../common/LoadingSkeleton';
import { sanitizeString, sanitizeError, sanitizeUserMessage } from '../../utils/sanitization';
import { validateString, validateNumber } from '../../utils/validation';

// Security: Enhanced sanitize message content using shared utility
function sanitizeMessageContent(content: string): string {
  if (!content || typeof content !== 'string') return '';
  // Use shared sanitizeUserMessage which uses DOMPurify
  const sanitized = sanitizeUserMessage(content);
  // Limit length
  return sanitized.slice(0, 2000);
}

function validateFile(file: File): { valid: boolean; error?: string } {
  // Security: File validation
  const maxSize = 10 * 1024 * 1024; // 10MB limit
  const allowedTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];
  
  const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.doc', '.docx', '.txt'];
  
  // Check file size
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }
  
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'File type not allowed' };
  }
  
  // Check file extension matches MIME type
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!allowedExtensions.includes(extension)) {
    return { valid: false, error: 'File extension not allowed' };
  }
  
  // Check for suspicious file names
  const suspiciousPatterns = [
    /\.exe$/i, /\.bat$/i, /\.cmd$/i, /\.scr$/i, /\.pif$/i,
    /\.com$/i, /\.vbs$/i, /\.js$/i, /\.jar$/i, /\.app$/i
  ];
  
  if (suspiciousPatterns.some(pattern => pattern.test(file.name))) {
    return { valid: false, error: 'File type not allowed' };
  }
  
  return { valid: true };
}

// Use shared sanitizeString for file names
function sanitizeFileName(fileName: string): string {
  return sanitizeString(fileName, 100) || '';
}

// Security: Enhanced rate limiting for message sending
class MessageRateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts = 10; // 10 messages per minute
  private readonly windowMs = 60000; // 1 minute
  private readonly maxAttemptsPerHour = 100; // 100 messages per hour
  private readonly hourlyWindowMs = 3600000; // 1 hour

  isAllowed(key: string): { allowed: boolean; message?: string } {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    const hourlyAttempts = attempts.filter(time => now - time < this.hourlyWindowMs);
    
    // Check hourly limit first
    if (hourlyAttempts.length >= this.maxAttemptsPerHour) {
      return {
        allowed: false,
        message: 'Too many messages in the last hour. Please wait before trying again.'
      };
    }
    
    if (recentAttempts.length >= this.maxAttempts) {
      return {
        allowed: false,
        message: 'Too many messages. Please try again later.'
      };
    }
    
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return { allowed: true };
  }

  getRemainingAttempts(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }
}

const messageRateLimiter = new MessageRateLimiter();

interface MessageInputProps {
  onSendMessage: (text: string, file?: File) => void;
  onTypingChange?: (isTyping: boolean) => void;
  isSending?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onTypingChange,
  isSending = false,
  placeholder = "Type your message...",
  disabled = false
}) => {
  const [messageText, setMessageText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attachmentMenuRef = useRef<HTMLDivElement>(null);

  // Security: Auto-resize textarea with safe DOM manipulation
  useEffect(() => {
    try {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
      }
    } catch (error) {
      // Security: Handle DOM manipulation errors gracefully
      // SECURITY: Removed console.warn to prevent information leakage
    }
  }, [messageText]);

  // Close attachment menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(event.target as Node)) {
        setShowAttachmentMenu(false);
      }
    };

    if (showAttachmentMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAttachmentMenu]);

  // Handle typing detection
  const handleTyping = (typing: boolean) => {
    setIsTyping(typing);
    onTypingChange?.(typing);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    if (typing) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        onTypingChange?.(false);
      }, 3000);
    }
  };

  const handleSend = () => {
    if ((!messageText.trim() && !selectedFile) || isSending || disabled) return;
    
    // Security: Enhanced rate limiting with validated key
    // SECURITY: Use a consistent, validated key for rate limiting
    const rateLimitKey = 'message_send_global';
    // SECURITY: Validate key before using
    if (!rateLimitKey || typeof rateLimitKey !== 'string' || rateLimitKey.length > 100) {
      setRateLimitError('Invalid rate limit configuration');
      setTimeout(() => setRateLimitError(null), 3000);
      return;
    }
    
    const rateLimitResult = messageRateLimiter.isAllowed(rateLimitKey);
    if (!rateLimitResult.allowed) {
      // SECURITY: Use state-based error display instead of alert() to prevent XSS
      const errorMessage = rateLimitResult.message || 'Too many messages sent. Please wait a moment before sending another message.';
      setRateLimitError(errorMessage);
      setTimeout(() => setRateLimitError(null), 5000);
      return;
    }
    
    // Clear any previous rate limit errors
    setRateLimitError(null);
    
    // Security: Enhanced validate message content
    const messageValidation = validateString(messageText, 'Message text', 0, 2000);
    if (!messageValidation.valid && !selectedFile) {
      // SECURITY: Removed console.warn to prevent information leakage
      return;
    }
    
    // Security: Enhanced sanitize message content
    const sanitizedMessage = sanitizeMessageContent(messageText.trim());
    
    // Removed debug log to prevent excessive console output
    
    onSendMessage(sanitizedMessage, selectedFile || undefined);
    setMessageText('');
    setSelectedFile(null);
    setFileError(null);
    setIsTyping(false);
    onTypingChange?.(false);
    
    // Removed debug log to prevent excessive console output
    
    // Security: Reset textarea height with error handling
    try {
      if (inputRef.current) {
        inputRef.current.style.height = '36px';
      }
    } catch (error) {
      // SECURITY: Removed console.warn to prevent information leakage
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Security: Enhanced validate file before setting
      const validation = validateFile(file);
      if (validation.valid) {
        // Removed debug log to prevent excessive console output
        
        setSelectedFile(file);
        setFileError(null);
        setShowAttachmentMenu(false);
      } else {
        // SECURITY: Removed console.warn to prevent information leakage
        setFileError(validation.error || 'Invalid file');
        setSelectedFile(null);
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const removeSelectedFile = () => {
    // Removed debug log to prevent excessive console output
    
    setSelectedFile(null);
    setFileError(null);
    try {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      // SECURITY: Removed console.warn to prevent information leakage
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full">
      {/* Security: Rate limit error display */}
      {rateLimitError && (
        <div className="mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800">{rateLimitError}</p>
        </div>
      )}
      
      {/* Security: File error display */}
      {fileError && (
        <div className="mb-3 p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-600">{fileError}</p>
        </div>
      )}
      
      {/* Security: File preview with sanitized data */}
      {selectedFile && (
        <div className="mb-3 p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                {selectedFile.type.startsWith('image/') ? (
                  <Image className="w-4 h-4 text-red-600" />
                ) : (
                  <FileText className="w-4 h-4 text-red-600" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                  {sanitizeFileName(selectedFile.name)}
                </p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
            <button
              onClick={removeSelectedFile}
              className="p-1 rounded-full hover:bg-red-100 transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex items-end gap-2 p-2 sm:p-3 bg-white">
        {/* Emoji button - Touch-friendly on mobile */}
        <button 
          className="p-2 sm:p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 shadow-sm flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
          disabled={disabled}
          aria-label="Add emoji"
        >
          <Smile className="w-5 h-5 sm:w-4 sm:h-4 text-gray-600" />
        </button>
        
        {/* Attachment button - Touch-friendly on mobile */}
        <div className="relative">
          <button 
            onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
            className="p-2 sm:p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 shadow-sm flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
            disabled={disabled}
            aria-label="Attach file"
          >
            <Paperclip className="w-5 h-5 sm:w-4 sm:h-4 text-gray-600" />
          </button>
          
          {/* Attachment menu */}
          {showAttachmentMenu && (
            <div ref={attachmentMenuRef} className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-xl border border-gray-200 p-2 min-w-[180px] z-50">
              <button
                onClick={openFileDialog}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Image className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-sm text-gray-700 font-medium">Photo</span>
              </button>
              <button
                onClick={openFileDialog}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gradient-to-r hover:from-green-50 hover:to-green-100 transition-all duration-200"
              >
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm text-gray-700 font-medium">Document</span>
              </button>
              <button className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gradient-to-r hover:from-purple-50 hover:to-purple-100 transition-all duration-200">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Mic className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-sm text-gray-700 font-medium">Voice</span>
              </button>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx"
          />
        </div>
        
        {/* Security: Message input with validation */}
        <div className="flex-1 min-w-0">
          <div className="relative">
            <textarea
              ref={inputRef}
              value={messageText}
              onChange={(e) => {
                // Security: Enhanced validate and sanitize input
                const inputValidation = validateString(e.target.value, 'Message input', 0, 2000);
                if (!inputValidation.valid) {
                  // SECURITY: Removed console.warn to prevent information leakage
                  return;
                }
                
                // Security: Enhanced sanitize input and limit length
                const sanitizedValue = sanitizeMessageContent(e.target.value);
                setMessageText(sanitizedValue);
                handleTyping(true);
              }}
              onKeyPress={handleKeyPress}
              onFocus={() => handleTyping(true)}
              onBlur={() => handleTyping(false)}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={2000}
              className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#da2516] shadow-sm w-full pr-16"
              style={{ minHeight: '36px', maxHeight: '100px', lineHeight: '1.4' }}
              rows={1}
            />
            {/* Character count positioned inside textarea with better padding */}
            <div className="absolute bottom-2 right-3 text-xs text-gray-400 pointer-events-none">
              {Math.min(messageText.length, 2000)}/2000
            </div>
          </div>
        </div>
        
        {/* Send button - Touch-friendly on mobile */}
        <button
          onClick={handleSend}
          disabled={(!messageText.trim() && !selectedFile) || isSending || disabled}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] w-11 h-11 md:w-11 md:h-11 rounded-xl bg-[#da2516] text-white hover:bg-[#b91c1c] active:bg-[#991b1b] transition-colors focus:ring-2 focus:ring-[#da2516] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Send message"
        >
          {isSending ? (
            <InlineSkeleton size="md" className="bg-white/50 rounded-full" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export default MessageInput;

